import { z } from 'zod';
import { idSchema, isoDateSchema } from '../primitives/branded.js';
import { priceSchema } from '../primitives/numeric.js';
import { toPatchSchema } from '../primitives/patch.js';
import { timestampSchema } from '../primitives/timestamp.js';
/**
 * Chart drawings.
 *
 * Every drawing is anchored in *data* coordinates (trading day + price), never in
 * pixels, so a stored drawing survives zooming, panning and window resizes. The
 * renderer converts anchors to pixels on each frame; dragging converts back.
 *
 * Adding a drawing type means adding one member to `drawingPayloadSchema` plus one
 * entry in the web-side renderer registry, which is typed against this union.
 */
/** A point in chart space: a trading day on the x-axis, a price on the y-axis. */
export const anchorSchema = z.object({
    date: isoDateSchema,
    price: priceSchema,
});
export const lineStyleSchema = z.enum(['solid', 'dashed', 'dotted']);
/** Visual style shared by all drawing types. */
export const drawingStyleSchema = z.object({
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Farbe muss ein Hex-Wert wie #2962ff sein')
        .default('#2962ff'),
    /**
     * Derzeit **ungenutzt**: Die Strichstärke folgt dem Typ, siehe
     * `DRAWING_LINE_WIDTH`. Das Feld bleibt im Schema, weil es in gespeicherten
     * Zeichnungen steht und eine spätere Einstellmöglichkeit darauf aufsetzen
     * würde. Wer die Stärke ändern will, ändert sie dort — nicht hier.
     */
    lineWidth: z.number().int().min(1).max(5).default(2),
    lineStyle: lineStyleSchema.default('solid'),
    /** Füllung flächiger Zeichnungen wie des Tunnels. 0 schaltet sie ab. */
    fillOpacity: z.number().min(0).max(1).default(0.12),
    /**
     * Deckkraft der gesamten Zeichnung.
     *
     * Hilfslinien treten damit hinter die Kurse zurück, ohne sie zu verdecken. Nicht
     * unter `MIN_DRAWING_OPACITY`, damit eine Zeichnung nicht versehentlich
     * unsichtbar wird und dann unauffindbar ist.
     *
     * Die Stop-Loss-Linie nimmt davon ausdrücklich nichts an: Sie zeigt den Kurs, zu
     * dem verkauft würde, und darf nicht zurücktreten.
     */
    opacity: z.number().min(0.15).max(1).default(1),
});
/** Untergrenze der Deckkraft; darunter wäre eine Zeichnung praktisch verschwunden. */
export const MIN_DRAWING_OPACITY = 0.15;
/** Zeichnungstypen, deren Deckkraft sich einstellen lässt. */
export const supportsOpacity = (type) => type !== 'stop_loss';
export const drawingTypeSchema = z.enum(['stop_loss', 'horizontal_line', 'trend_line', 'channel']);
export const DRAWING_TYPE_LABELS = {
    stop_loss: 'Stop-Loss-Linie',
    horizontal_line: 'Unterstützung',
    trend_line: 'Trendlinie',
    channel: 'Tunnel',
};
/**
 * Zeichnungstypen, die es je Chart nur einmal geben darf.
 *
 * Die Stop-Loss-Linie bildet den Stop-Loss-Wert der Position ab. Zwei davon
 * könnten verschiedene Kurse zeigen, von denen nur einer gilt — die Beschränkung
 * verhindert einen Widerspruch, der sich nicht auflösen ließe.
 */
export const SINGLETON_DRAWING_TYPES = ['stop_loss'];
/**
 * Zeichenreihenfolge: kleinere Werte liegen weiter hinten.
 *
 * Die Reihenfolge ergibt sich aus der Bedeutung, nicht aus dem Zeitpunkt der
 * Erstellung:
 *
 *  - Der **Tunnel** hat eine Füllfläche und liegt deshalb ganz hinten — sonst
 *    legt er sich als Schleier über alles, was zuvor gezeichnet wurde.
 *  - **Unterstützungen und Trendlinien** darüber, auf derselben Ebene: Beide sind
 *    Hilfslinien aus derselben Analyse, nur einmal waagerecht und einmal schräg.
 *  - Die **Stop-Loss-Linie** immer zuoberst. Sie zeigt den Kurs, zu dem verkauft
 *    würde; sie darf von keiner Hilfslinie verdeckt werden.
 *
 * Gilt gleichermaßen für das Zeichnen und für die Treffererkennung: Was oben
 * liegt, wird auch zuerst angefasst.
 */
export const DRAWING_Z_ORDER = {
    channel: 0,
    horizontal_line: 1,
    trend_line: 1,
    stop_loss: 2,
};
/**
 * Strichstärke je Typ, in Pixeln.
 *
 * Eine Eigenschaft der **Bedeutung**, nicht der einzelnen Zeichnung: Die
 * Stop-Loss-Linie ist kräftiger, weil sie den Kurs zeigt, zu dem verkauft würde.
 * Alles andere sind Hilfslinien aus der Analyse und sollen hinter die Kerzen
 * zurücktreten, statt mit ihnen zu konkurrieren.
 *
 * Deshalb hier und nicht in `style.lineWidth`: Dieses Feld ist zwar gespeichert,
 * aber in der Oberfläche gar nicht einstellbar — die Stärke wäre dort eine Wahl,
 * die niemand trifft. Derselbe Gedanke wie bei der Deckkraft, die die
 * Stop-Loss-Linie ebenfalls unabhängig vom Stil auf voll setzt.
 *
 * Bei Auswahl kommt in den Zeichnungen ein Pixel hinzu, damit erkennbar bleibt,
 * woran gerade gearbeitet wird.
 */
/**
 * Die Farbe der Stop-Loss-Linie. Nicht wählbar.
 *
 * Sie ist die eine Linie, die auf jedem Chart dieselbe Bedeutung hat — und zwar
 * eine, bei der man sich nicht vertun darf. Eine feste Farbe macht sie über alle
 * Werte hinweg wiedererkennbar, noch bevor man die Beschriftung liest.
 *
 * Aus demselben Grund steht sie den übrigen Indikatoren **nicht** zur Verfügung:
 * Wäre eine Trendlinie ebenfalls blau, ginge genau die Wiedererkennung verloren.
 * Eine Ausnahme gilt für verlinkte Indikatoren — die übernehmen sie absichtlich,
 * weil sie dann zum Stop-Loss gehören.
 */
export const STOP_LOSS_COLOR = '#2962ff';
export const DRAWING_LINE_WIDTH = {
    stop_loss: 2,
    horizontal_line: 1,
    trend_line: 1,
    channel: 1,
};
/**
 * (1) Stop-Loss-Linie: eine waagerechte Marke auf einem festen Kurs.
 *
 * Sie ist an den Stop-Loss-Wert der Position gekoppelt: Wird sie verschoben,
 * übernimmt die Position den neuen Kurs als festen Stop; wird der Stop-Loss
 * anderswo geändert, wandert die Linie mit. Deshalb darf es je Chart nur eine
 * geben.
 *
 * (1.1) Mit `distanceLeg` wird eine senkrechte Verbindung zum letzten Schlusskurs
 * gezogen, beschriftet mit dem Abstand absolut und in Prozent. Ihre x-Position
 * folgt stets der jüngsten Kerze und wird daher nicht gespeichert, sondern bei
 * jedem Frame abgeleitet.
 */
const stopLossPayloadSchema = z.object({
    type: z.literal('stop_loss'),
    price: priceSchema,
    /** Zeichnet die senkrechte Verbindung zum letzten Schlusskurs. */
    distanceLeg: z.boolean().default(true),
});
/**
 * (1a) Unterstützung: eine waagerechte Linie auf einem frei gewählten Kurs.
 *
 * Dieselbe Form wie die Stop-Loss-Linie, aber ohne deren Bindung: Sie gehört zur
 * Analyse und nicht zur Position, verändert also nichts am Stop und darf beliebig
 * oft vorkommen. Ein Chart trägt typischerweise mehrere — eine je Kursniveau, an
 * dem der Kurs mehrfach gedreht hat.
 *
 * Kein `distanceLeg` wie beim Stop-Loss: Der Abstand zum aktuellen Kurs ist dort
 * die Kennzahl, um die es geht. Bei einer Unterstützung interessiert, ob der Kurs
 * das Niveau hält — nicht, wie weit er davon entfernt ist.
 */
const horizontalLinePayloadSchema = z.object({
    type: z.literal('horizontal_line'),
    price: priceSchema,
});
/** (2) Diagonal support/resistance line between two anchors. */
const trendLinePayloadSchema = z.object({
    type: z.literal('trend_line'),
    from: anchorSchema,
    to: anchorSchema,
    /** Continue the line beyond its anchors towards the chart edges. */
    extendLeft: z.boolean().default(false),
    extendRight: z.boolean().default(true),
});
/**
 * (3) Diagonal channel: a base line plus a parallel line offset by `offset` in
 * price units. Storing an offset instead of a second pair of anchors guarantees
 * the two lines stay parallel no matter which handle the user drags.
 */
const channelPayloadSchema = z.object({
    type: z.literal('channel'),
    from: anchorSchema,
    to: anchorSchema,
    /** Signed price distance of the parallel line from the base line. */
    offset: z.number().finite(),
    extendLeft: z.boolean().default(false),
    extendRight: z.boolean().default(true),
});
export const drawingPayloadSchema = z.discriminatedUnion('type', [
    stopLossPayloadSchema,
    horizontalLinePayloadSchema,
    trendLinePayloadSchema,
    channelPayloadSchema,
]);
export const drawingSchema = z.object({
    id: idSchema,
    instrumentId: idSchema,
    label: z.string().max(120).nullable(),
    /** Render the price the drawing resolves to next to its label. */
    showPrice: z.boolean(),
    style: drawingStyleSchema,
    payload: drawingPayloadSchema,
    /**
     * Die Stop-Loss-Linie, mit der dieser Indikator verbunden ist.
     *
     * Die drei folgenden Felder stehen am **verlinkten** Indikator, nicht an der
     * Stop-Loss-Linie: „Bin ich verbunden?" ist eine Eigenschaft dessen, den es
     * betrifft. Die Stop-Loss-Linie bleibt unverändert und muss von der Verbindung
     * nichts wissen.
     */
    linkedDrawingId: idSchema.nullable(),
    /**
     * Die Farbe vor dem Verlinken.
     *
     * Ein verlinkter Indikator übernimmt das Blau der Stop-Loss-Linie — er gehört
     * dann sichtbar zu ihr. Beim Lösen soll er seine eigene Farbe zurückbekommen,
     * und die weiß danach niemand mehr, wenn sie nicht aufgehoben wurde.
     */
    originalColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable(),
    /**
     * Der Abstand, der als 100 % gilt.
     *
     * Festgehalten beim Verlinken, beim Ändern eines der beiden und beim Einbuchen.
     * Auf Stop-Loss-Seite zählt dabei der **eingebuchte** Kurs, nicht die Linie: Die
     * Order beim Broker ist das, was tatsächlich greift.
     */
    referenceDistance: z.number().finite().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
/**
 * Änderungen am Stil.
 *
 * Ohne Vorgabewerte: Ein Patch darf nur enthalten, was tatsächlich geändert wird —
 * sonst überschreibt er gespeicherte Werte mit Vorgaben. Siehe `toPatchSchema`.
 */
export const drawingStylePatchSchema = toPatchSchema(drawingStyleSchema);
export const createDrawingSchema = z.object({
    label: z.string().max(120).nullish(),
    showPrice: z.boolean().default(true),
    /* Beim Anlegen sind Vorgabewerte richtig. */
    style: drawingStyleSchema.partial().optional(),
    payload: drawingPayloadSchema,
});
/**
 * Updates carry the whole payload. A drag produces a new geometry, and replacing
 * the payload wholesale keeps the discriminated union intact — a partial payload
 * could otherwise mix fields from two drawing types.
 */
export const updateDrawingSchema = z.object({
    label: z.string().max(120).nullish(),
    showPrice: z.boolean().optional(),
    style: drawingStylePatchSchema.optional(),
    payload: drawingPayloadSchema.optional(),
});
//# sourceMappingURL=drawing.js.map